import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Label } from "@/components/ui/label";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarInput,
} from "@/components/ui/sidebar";
import { routes } from "@/config/routes-config";
import { GroupsEnum } from "@/enum/groups";
import { useAuthData } from "@/context/auth-data-provider";
import type { SearchEntry } from "@/@types/sidebar-search";
import type { SearchableRoute, UserWithPermissions } from "@/@types/tab-navigation";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { buildSearchEntries, matchSearchEntries } from "./search-entries";

const SEARCHABLE_ROUTES: SearchableRoute[] = routes;

function useClickOutside(
  ref: React.RefObject<HTMLElement>,
  onClickOutside: () => void
) {
  useEffect(() => {
    function handler(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside();
      }
    }

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClickOutside]);
}

export function SearchForm({ ...props }: React.ComponentProps<"form">) {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const authData = useAuthData();
  const userGroups: GroupsEnum[] = useMemo(
    (): GroupsEnum[] => (authData?.groupNames ?? []) as GroupsEnum[],
    [authData?.groupNames],
  );
  const cachedUser: UserWithPermissions | null = useMemo(
    (): UserWithPermissions | null => getUserFromLocalStorage(),
    [],
  );
  const user: UserWithPermissions | null = authData?.user ?? cachedUser;

  const searchEntries: SearchEntry[] = useMemo(
    (): SearchEntry[] => buildSearchEntries(SEARCHABLE_ROUTES, userGroups, user),
    [userGroups, user],
  );

  const filteredEntries: SearchEntry[] = useMemo(
    (): SearchEntry[] => matchSearchEntries(searchEntries, searchTerm),
    [searchEntries, searchTerm],
  );

  const isDropdownOpen = filteredEntries.length > 0;

  useClickOutside(dropdownRef, () => setSearchTerm(""));

  const handleEntryClick = useCallback(
    (entry: SearchEntry): void => {
      navigate(entry.url);
      setSearchTerm("");
    },
    [navigate]
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <form {...props} onSubmit={(e) => e.preventDefault()}>
        <SidebarGroup className="py-0">
          <SidebarGroupContent className="relative">
            <Label htmlFor="search" className="sr-only">
              Procurar
            </Label>
            <SidebarInput
              id="search"
              placeholder="Procurar Projeto..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 select-none opacity-50" />
          </SidebarGroupContent>
        </SidebarGroup>
      </form>

      {isDropdownOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-sidebar shadow-lg">
          <ul className="max-h-60 overflow-auto py-1">
            {filteredEntries.map((entry: SearchEntry, index: number) => (
              <li
                key={`${entry.key}-${index}`}
                className="flex cursor-pointer items-center px-3 py-2 hover:bg-sidebar-accent"
                onClick={() => handleEntryClick(entry)}
              >
                <div className="mr-2 text-sidebar-foreground">{entry.icon}</div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{entry.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {entry.description}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
